<template>
  <TabsBuilder
    :isDarkMode="isDarkMode"
    :navigationDescription="componentData.description"
    :isFullPageTabs="componentData.fullPageTabs"
  >
    <li
      v-for="(tabItem, index) in items"
      slot="tab-bar"
      :key="tabItem.id"
      class="owc-tabs__item"
      :class="{
        'owc-tabs__item--active': tabItem.isActive,
        'owc-tabs__item--dark': isDarkMode,
      }"
      @click="setActiveTab(tabItem, index)"
    >
      <button
        class="wb-button wb-button--tertiary wb-button--medium"
        :class="{
          'wb-button--theme-dark': isDarkMode,
        }"
        :aria-current="tabItem.isActive ? 'page' : false"
      >
        {{ tabItem.label }}
      </button>
    </li>

    <ValidationBoxWrapper v-if="isEditMode" slot="validation-box">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>

    <TabAuthor v-if="isAuthorMode" slot="tab-content" :tabItems="items" />
    <SlotContainer
      v-else
      refName="slot-tab-content"
      slot="tab-content"
      :slotName="activeTabId"
      :isReactive="true"
      :slot-component-data="JSON.stringify({ tabIndex: generateTabIndex })"
    >
      <slot :slot="activeTabId" :name="activeTabId"></slot>
    </SlotContainer>
  </TabsBuilder>
</template>

<script lang="ts">
/**
 * `Tabs` - Tabs are used to divide content into meaningful, related sections.
 * Tabs allow users to focus on one specific view at a time while maintaining
 * sight of all the relevant content options available. Each Tab, when active,
 * will reveal its own unique content..
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import { isAemAuthorMode } from './TabsUtils';
import TabsBuilder from '@owc-packages/TabsBuilder/src/TabsBuilder.vue';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import { TabsComponent, TabItem } from './Tabs';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';

export default (ComponentInitializeDispatcher as TabsComponent).extend({
  name: 'owc-tabs',
  components: {
    TabsBuilder,
    SlotContainer,
    TabAuthor: () => import('./TabAuthor.vue'),
    ValidationBoxWrapper,
  },
  mixins: [OwcDataComponent],
  mounted() {
    const initializedItems = this.componentData?.items.map((item, index) => {
      return { ...item, isActive: index === 0 } as TabItem;
    });
    if (initializedItems && initializedItems.length) {
      this.items = initializedItems;
    }

    this.activeTabId = this.items[0].id;
  },
  data() {
    return {
      items: [],
      activeTabId: '',
      currentTabIndex: 1,
    };
  },
  computed: {
    isDarkMode: function() {
      return Boolean(this.componentData?.darkMode);
    },
    isAuthorMode: function() {
      return isAemAuthorMode();
    },
    generateTabIndex: function() {
      return this.currentTabIndex++;
    },
  },
  methods: {
    ensureSliderRender(clickedTab: TabItem) {
      if (!this.isBuildWc) {
        return;
      }

      // update to wb-slider can only be called when the container has width
      const intervalId = window.setInterval(() => {
        /**
         * Small Fix to display the side arrows (if they are active).
         * This only occurs on wc environment.
         */
        const tabId = clickedTab.id;
        const currentTabSlotComponents = document.querySelectorAll(`[slot='${tabId}'] > *`);

        if (currentTabSlotComponents && currentTabSlotComponents.length) {
          currentTabSlotComponents.forEach(slotComponent => {
            const slotShadowRoot = slotComponent.shadowRoot;
            const sliders = (slotShadowRoot ?? slotComponent)?.querySelectorAll('wb-slider') as NodeListOf<
              HTMLWbSliderElement
            >;
            if (sliders?.length) {
              sliders.forEach((slider: HTMLWbSliderElement) => slider.update());
            }
            window.clearInterval(intervalId);
          });
        }
      }, 100);
    },
    setActiveTab(clickedTab: TabItem, index: number) {
      /*
       * Since arrays are not reactive, we update
       * every tab's isActive value manually.
       * This could be done in a more performant way, by storing
       * the previous active value and deactivating only that one
       * but this is a more clean solution and there will never be
       * enough tabs for this to be an issue (performance wise)
       */
      this.items = this.items.map(tabItem => {
        return { ...tabItem, isActive: tabItem.id === clickedTab.id };
      });
      this.activeTabId = clickedTab.id;
      const tabIndex = index + 1;
      const payload = `tab${tabIndex}:${clickedTab.label}`;

      this.handleCustomTracking(this.$el, payload, 'link', 'click');

      this.ensureSliderRender(clickedTab);
    },
  },
});
</script>

<style lang="scss">
.owc-tabs {
  &__item {
    $this: &;
    display: block;
    border: none;

    &--active {
      .wb-button {
        border-bottom: 2px solid var(--wb-blue-45);

        color: var(--wb-grey-20);
      }
    }
    &--dark {
      .wb-button {
        color: var(--wb-grey-70);
      }
      &#{$this}--active {
        .wb-button {
          color: var(--wb-white);
        }
      }
    }
    .wb-button {
      width: 100%;
      min-width: 5rem;
      border-radius: 0;

      color: var(--wb-grey-45);

      // keeps tab text on a single line
      white-space: nowrap;

      &:focus {
        border-color: var(--wb-blue-45);
      }
    }
  }
}
</style>
